import {useEffect, useState} from 'react';
import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {ProgressCircle} from '@ui/progress/progress-circle';
import {
  recentlyReloadedForStaleChunk,
  reloadForStaleChunk,
} from '@app/stale-chunk-recovery';

/**
 * Shown instead of an error page when a page's code could not be loaded
 * because the site was updated while this tab was open. Reloads once on its
 * own; if that already happened (or the connection is down) it says so calmly
 * and offers a button, never a stack trace.
 */
export function AppUpdatedMessage() {
  const [state, setState] = useState<'refreshing' | 'failed'>(
    recentlyReloadedForStaleChunk() ? 'failed' : 'refreshing',
  );

  useEffect(() => {
    if (state !== 'refreshing') return;
    reloadForStaleChunk().then(reloading => {
      if (!reloading) setState('failed');
    });
  }, [state]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-16 py-40">
      <div className="max-w-440 text-center">
        {state === 'refreshing' ? (
          <>
            <ProgressCircle isIndeterminate className="mx-auto mb-20" />
            <h1 className="mb-8 text-xl font-semibold">
              <Trans message="The application has been updated" />
            </h1>
            <p className="text-sm text-muted">
              <Trans message="We're refreshing your session to load the latest version." />
            </p>
          </>
        ) : (
          <>
            <span className="material-icons mb-12 text-5xl text-primary">system_update_alt</span>
            <h1 className="mb-8 text-xl font-semibold">
              <Trans message="A new version is available" />
            </h1>
            <p className="mb-20 text-sm text-muted">
              <Trans message="This page could not be loaded. Check your connection, then reload to get the latest version." />
            </p>
            <Button variant="flat" color="primary" onClick={() => window.location.reload()}>
              <Trans message="Reload" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
